import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, ChevronRight, ChevronLeft, ArrowLeft, ArrowRight as ArrowRightIcon } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useI18n } from "../lib/i18n";

const LABELS = {
  marketplace: { en: "Marketplace", ar: "السوق" },
  rentals: { en: "Rentals", ar: "التأجير" },
  auctions: { en: "Auctions", ar: "المزادات" },
  financing: { en: "Financing", ar: "التمويل" },
  insurance: { en: "Insurance", ar: "التأمين" },
  inspection: { en: "Inspection", ar: "الفحص" },
  delivery: { en: "Delivery", ar: "التوصيل" },
  parts: { en: "Spare Parts", ar: "قطع الغيار" },
  services: { en: "Maintenance", ar: "الصيانة" },
  salvage: { en: "Salvage", ar: "السكراب" },
  export: { en: "Export", ar: "التصدير" },
  heavy: { en: "Heavy Equipment", ar: "المعدات الثقيلة" },
  motorcycles: { en: "Motorcycles", ar: "الدراجات" },
  marine: { en: "Yachts & Boats", ar: "يخوت وزوارق" },
  fleet: { en: "Corporate Fleet", ar: "أساطيل الشركات" },
  disputes: { en: "Dispute Center", ar: "النزاعات" },
  trust: { en: "Trust & Safety", ar: "الأمان والثقة" },
  pricing: { en: "Pricing", ar: "الباقات" },
  dashboard: { en: "Dashboard", ar: "لوحة التحكم" },
  messages: { en: "Messages", ar: "الرسائل" },
  favorites: { en: "Favorites", ar: "المفضلة" },
  "saved-searches": { en: "Saved Searches", ar: "البحوث المحفوظة" },
  sell: { en: "Sell a Vehicle", ar: "بيع مركبة" },
  vehicle: { en: "Vehicle", ar: "مركبة" },
  login: { en: "Sign in", ar: "تسجيل الدخول" },
  register: { en: "Join", ar: "انضمام" },
  terms: { en: "Terms of Use", ar: "شروط الاستخدام" },
  privacy: { en: "Privacy Policy", ar: "سياسة الخصوصية" },
  disclaimer: { en: "Disclaimer", ar: "إخلاء المسؤولية" },
  "refund-policy": { en: "Refund Policy", ar: "سياسة الاسترداد" },
  "fraud-policy": { en: "Fraud Prevention", ar: "منع الاحتيال" },
  deal: { en: "Complete Deal", ar: "إتمام الصفقة" },
  "pre-purchase-inspection": { en: "Pre-purchase Inspection", ar: "فحص قبل الشراء" },
  "periodic-inspection": { en: "Periodic Inspection", ar: "فحص دوري" },
  "vehicle-history": { en: "Vehicle History", ar: "تقرير السيارة" },
  "ownership-transfer": { en: "Ownership Transfer", ar: "نقل الملكية" },
  "other-services": { en: "Service Providers Portal", ar: "بوابة مزودي الخدمات" },
  workshop: { en: "Comprehensive service centres", ar: "مراكز الصيانة الشاملة" },
  "car-wash": { en: "Car-care centres", ar: "مراكز العناية بالسيارات" },
  orders: { en: "My Orders", ar: "طلباتي" },
};

export default function Breadcrumbs({ trail }) {
  const { lang } = useI18n();
  const loc = useLocation();
  const navigate = useNavigate();
  if (loc.pathname === "/") return null;

  // Hide opaque IDs (UUIDs, ObjectIds, long hex/alnum tokens) from the
  // breadcrumb trail — the user should never see raw internal identifiers.
  const isOpaqueId = (s) => {
    if (!s) return false;
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return true; // uuid
    if (/^[0-9a-f]{24}$/i.test(s)) return true; // mongo ObjectId
    if (/^[A-Za-z0-9_-]{16,}$/.test(s) && !LABELS[s]) return true; // long opaque token
    return false;
  };
  const parts = (trail || loc.pathname.split("/").filter(Boolean)).filter((p) => !isOpaqueId(p));
  const Chevron = lang === "ar" ? ChevronLeft : ChevronRight;
  const BackArrow = lang === "ar" ? ArrowRightIcon : ArrowLeft;

  const handleBack = () => {
    // Prefer the in-app history when there is something to go back to.
    // Fall back to Home when the user landed directly on a deep link.
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  // BreadcrumbList JSON-LD mirroring the visible trail; vehicle/company pages emit their own.
  const crumbs = parts.slice(0, 3);
  const ldSkip = ["vehicle", "vehicles", "company"].includes(crumbs[0]);
  const ld = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [{ name: lang === "ar" ? "الرئيسية" : "Home", path: "/" }]
      .concat(crumbs.map((p, i) => ({ name: LABELS[p]?.[lang] || decodeURIComponent(p).replace(/-/g, " "), path: "/" + crumbs.slice(0, i + 1).join("/") })))
      .map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: `https://cartal.sa${c.path}` })),
  };

  return (
    <nav
      aria-label="Breadcrumb"
      className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-4 text-sm"
      data-testid="breadcrumbs"
    >
      {!ldSkip && crumbs.length > 0 && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(ld)}</script>
        </Helmet>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold border border-[#0a0a0a]/15 bg-white hover:bg-[#0a0a0a] hover:text-white hover:border-[#0a0a0a] transition-colors"
          data-testid="nav-back-btn"
          aria-label={lang === "ar" ? "رجوع" : "Back"}
        >
          <BackArrow className="w-3.5 h-3.5" />
          <span>{lang === "ar" ? "رجوع" : "Back"}</span>
        </button>

        <ol className="flex items-center flex-wrap gap-1.5 text-[#525252]">
          <li>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 hover:text-[#0a0a0a] transition-colors"
              data-testid="breadcrumb-home"
            >
              <Home className="w-3.5 h-3.5" />
              <span>{lang === "ar" ? "الرئيسية" : "Home"}</span>
            </Link>
          </li>
          {parts.slice(0, 3).map((p, i) => {
            const isLast = i === parts.slice(0, 3).length - 1;
            const label = LABELS[p]?.[lang] || decodeURIComponent(p).replace(/-/g, " ");
            const to = "/" + parts.slice(0, i + 1).join("/");
            return (
              <React.Fragment key={p + i}>
                <li aria-hidden className="text-[#b3b3b3]"><Chevron className="w-3.5 h-3.5" /></li>
                <li>
                  {isLast ? (
                    <span className="text-[#0a0a0a] font-medium truncate max-w-[260px] inline-block align-bottom">{label}</span>
                  ) : (
                    <Link to={to} className="hover:text-[#0a0a0a] transition-colors">{label}</Link>
                  )}
                </li>
              </React.Fragment>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}
